import { Stack, Table, TableHead, TableBody, TableRow, Grid } from '@mui/material';
import MuiButton from '@mui/material/Button';
import React, { useState } from 'react';

import { ShipmentPackageRow } from './ShipmentPackageRow';
import {
  ShipmentPackagesListProps,
  SelectedShipmentPackage,
  FreightPackageType,
} from './ShipmentPackagesList.props';
import {
  actionColumnSx,
  BoldLabel,
  dimensionColumnSx,
  quantityColumnSx,
  StyledTableCell,
  weightColumnSx,
} from './ShipmentPackagesList.styles';
import { dangerBtnSx, outlinedBtnSx } from '../../../configs/detailPanelStyles';
import { CustomShippingPackageType, FreightPackageTypeName } from '../../../configs/enums';
import {
  getShipmentPackageCriteriaIndex,
  getUpdatedShipmentPackageCriteriaList,
} from '../../../utils/shipment';

export const ShipmentPackagesList: React.FC<ShipmentPackagesListProps> = ({
  customPackageTypes,
  freightTypes,
  onChange,
}) => {
  const [selectedPackages, setSelectedPackages] = useState<Array<SelectedShipmentPackage>>([]);

  const onValueChange = (packageFeature: string, id: number, value: number) => {
    const updatedList = getUpdatedShipmentPackageCriteriaList(
      selectedPackages,
      packageFeature,
      id,
      value
    );
    setSelectedPackages(updatedList);
    onChange(updatedList);
  };

  const onPackageTypeChange = (id: number, value: string) => {
    const packageList = [...selectedPackages];
    const updatedRowIndex = getShipmentPackageCriteriaIndex(selectedPackages, id);
    packageList[updatedRowIndex].packageType = value;
    setSelectedPackages(packageList);
    onChange(packageList);
  };

  const onAddPackage = (packageData?: FreightPackageType) => {
    const packageList = [...selectedPackages];
    const packageItem = {
      id:
        selectedPackages.length > 0 ? Math.max(...selectedPackages.map((item) => item.id)) + 1 : 1,
      typeId: packageData?.id || 0,
      name: packageData?.name || FreightPackageTypeName.Custom,
      height: packageData?.height || 1,
      width: packageData?.width || 1,
      length: packageData?.length || 1,
      quantity: 1,
      weight: 1,
      packageType: !packageData?.name ? CustomShippingPackageType.Carton : '',
    };
    packageList.push(packageItem);
    setSelectedPackages(packageList);
    onChange(packageList);
  };

  const onQuantityStep = (id: number, direction: 1 | -1) => {
    const packageList = [...selectedPackages];
    const index = getShipmentPackageCriteriaIndex(packageList, id);
    // Quantity never drops below one — a package of nought is a removal.
    if (direction === -1 && packageList[index].quantity === 1) return;
    packageList[index].quantity += direction;
    setSelectedPackages(packageList);
    onChange(packageList);
  };

  const onRemove = (id: number) => {
    const updated = selectedPackages.filter((item) => item.id !== id);
    setSelectedPackages(updated);
    onChange(updated);
  };

  const onClear = () => {
    setSelectedPackages([]);
    onChange([]);
  };

  return (
    <Grid>
      <Table size="small">
        {/* Headers only once a package exists — a header row over nothing is a
            promise of content that isn't there. */}
        <TableHead sx={{ display: selectedPackages.length > 0 ? 'table-header-group' : 'none' }}>
          <TableRow>
            <StyledTableCell align="left">
              <BoldLabel>Type</BoldLabel>
            </StyledTableCell>
            <StyledTableCell align="left" sx={dimensionColumnSx}>
              <BoldLabel>Length (cm)</BoldLabel>
            </StyledTableCell>
            <StyledTableCell align="left" sx={dimensionColumnSx}>
              <BoldLabel>Width (cm)</BoldLabel>
            </StyledTableCell>
            <StyledTableCell align="left" sx={dimensionColumnSx}>
              <BoldLabel>Height (cm)</BoldLabel>
            </StyledTableCell>
            <StyledTableCell align="center" sx={quantityColumnSx}>
              <BoldLabel>Qty</BoldLabel>
            </StyledTableCell>
            <StyledTableCell align="left" sx={weightColumnSx}>
              <BoldLabel>Weight (kg)</BoldLabel>
            </StyledTableCell>
            <StyledTableCell align="left">
              <BoldLabel>Packaging Type</BoldLabel>
            </StyledTableCell>
            {/* The rows carry a delete button; without a matching header cell
                the browser had one fewer column up top and every width below
                it shifted left, which is how the bin ended up over the
                packaging picker. */}
            <StyledTableCell sx={actionColumnSx} aria-label="Actions" />
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedPackages.map((row) => (
            <ShipmentPackageRow
              key={row.id}
              row={row}
              customPackageTypes={customPackageTypes}
              onValueChange={onValueChange}
              onPackageTypeChange={onPackageTypeChange}
              onQuantityStep={onQuantityStep}
              onRemove={onRemove}
            />
          ))}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75} mt={1.5}>
        <MuiButton
          variant="outlined"
          size="small"
          sx={outlinedBtnSx}
          onClick={() => onAddPackage()}
        >
          Add custom
        </MuiButton>
        {freightTypes.map((row) => (
          <MuiButton
            key={row.id}
            variant="outlined"
            size="small"
            sx={outlinedBtnSx}
            onClick={() => onAddPackage(row)}
          >
            {`Add ${row.name} (${row.length} × ${row.width} × ${row.height} cm)`}
          </MuiButton>
        ))}
        {selectedPackages.length > 0 && (
          <MuiButton variant="outlined" size="small" sx={dangerBtnSx} onClick={onClear}>
            Clear all
          </MuiButton>
        )}
      </Stack>
    </Grid>
  );
};
